/**
 * Shared HTTP + error primitives for every module's API layer.
 *
 * Before Sprint 02 each module's `api/queries.ts` defined its own
 * `fetch + throw new Error(\`path \${status}: …\`)` shim. That masked
 * real HTTP status codes inside a generic Error, so the pages couldn't
 * distinguish "I'm 401 and the user needs to log in" from "endpoint
 * returned 500." Everyone rendered `data?.rows ?? []` and looked fine
 * while the backend was down.
 *
 * Now: one `jsonFetch` + `ApiError` with a `.status` property. Modules
 * swap their private fetch for this; pages wrap data-consuming sections
 * in `<ApiErrorBoundary>` (see `@/components/ui/ApiErrorBoundary`) which
 * renders a login prompt when `error.isAuthError === true` and falls
 * back to a generic error state otherwise.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  readonly status: number
  /** The raw `detail` field from a FastAPI error body, whatever shape it
   *  is — a string for the common case, or a structured object (e.g.
   *  `{error, limit, active, hint}` for a 409) for endpoints that return
   *  machine-readable detail. Undefined when the body wasn't JSON or had
   *  no `detail` key. `.message` above always stays a flattened string
   *  for log lines; use `.detail` when a caller wants to branch on the
   *  structured shape instead of parsing `.message` itself. */
  readonly detail?: unknown

  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
    this.name = 'ApiError'
  }

  /** 401 = session missing / expired. Frontend treats as "send the user
   *  back through Google OAuth", distinct from a 500 or network error. */
  get isAuthError(): boolean {
    return this.status === 401
  }
}

const NON_JSON_BODY_MAX_LENGTH = 200

/** Non-2xx responses that aren't JSON usually mean an infra layer in front
 *  of the app answered instead of FastAPI — nginx's own 502/504 error pages
 *  are full HTML documents. Embedding that verbatim in a toast is
 *  unreadable, so drop HTML bodies entirely and cap anything else to a
 *  short excerpt. */
function sanitizeNonJsonBody(text: string, contentType: string | null): string {
  const trimmed = text.trim()
  if (!trimmed) return ''
  if (contentType?.includes('text/html') || trimmed.startsWith('<')) return ''
  return trimmed.length > NON_JSON_BODY_MAX_LENGTH
    ? `${trimmed.slice(0, NON_JSON_BODY_MAX_LENGTH)}…`
    : trimmed
}

export interface JsonFetchInit extends Omit<RequestInit, 'body'> {
  /** JSON-serialized automatically when provided. Use `rawBody` for
   *  multipart / FormData. */
  body?: unknown
  /** Bypass the automatic JSON serialization — e.g. multipart uploads. */
  rawBody?: BodyInit
}

/**
 * One fetch helper the whole ops app uses. Adds `credentials: 'include'`
 * so the Google-OAuth session cookie gets sent, serializes JSON bodies,
 * and maps non-2xx responses into a typed `ApiError`.
 *
 * Does NOT auto-retry, auto-redirect on 401, or log — those concerns
 * live at the page level (ApiErrorBoundary) or in React Query options
 * per call site.
 */
export async function jsonFetch<T>(path: string, init: JsonFetchInit = {}): Promise<T> {
  const { body, rawBody, headers, ...rest } = init
  const resolvedHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(headers as Record<string, string> | undefined),
  }

  let resolvedBody: BodyInit | undefined
  if (rawBody !== undefined) {
    resolvedBody = rawBody
  } else if (body !== undefined) {
    resolvedHeaders['Content-Type'] ??= 'application/json'
    resolvedBody = JSON.stringify(body)
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...rest,
    headers: resolvedHeaders,
    body: resolvedBody,
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    // FastAPI error bodies are `{"detail": "..."}` — surface that string
    // directly instead of dumping the raw JSON blob at users. A Pydantic
    // 422 instead shapes `detail` as a list of {loc, msg, type} objects;
    // flatten those into one readable line too. Falls back to a sanitized
    // excerpt of the raw text when it isn't either shape — infra failures
    // (nginx 502/504, gateway timeouts) return HTML, not JSON, and dumping
    // that verbatim into a user-facing toast is unreadable.
    let detail: string | undefined
    let rawDetail: unknown
    if (text) {
      try {
        const parsed = JSON.parse(text) as unknown
        if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
          rawDetail = (parsed as { detail?: unknown }).detail
          if (typeof rawDetail === 'string') {
            detail = rawDetail
          } else if (Array.isArray(rawDetail)) {
            detail = rawDetail
              .map((item) => {
                if (item && typeof item === 'object' && typeof (item as { msg?: unknown }).msg === 'string') {
                  const loc = (item as { loc?: unknown }).loc
                  const field = Array.isArray(loc) ? loc.filter((p) => p !== 'body').join('.') : undefined
                  const msg = (item as { msg: string }).msg
                  return field ? `${field}: ${msg}` : msg
                }
                return null
              })
              .filter((s): s is string => Boolean(s))
              .join('; ') || undefined
          }
        }
      } catch {
        // not JSON — fall through to the sanitized-text fallback below
      }
    }
    // Keep the status + path prefix in the thrown message so log lines
    // + test assertions can match on the code without needing an
    // `instanceof ApiError` check. Body text follows after a colon.
    const body = detail ?? sanitizeNonJsonBody(text, res.headers?.get('content-type') ?? null)
    const message = body ? `${path} ${res.status}: ${body}` : `${path} ${res.status}`
    throw new ApiError(res.status, message, rawDetail)
  }

  // 204 has no body.
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Convenience: true when any query/mutation error from React Query
 *  represents an authentication failure. Tolerates non-Error shapes. */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && error.isAuthError
}
